import { useState, useCallback, useMemo } from 'react';
import { Container, Box, Typography, Button, Grid } from '@mui/material';
import { useCollections } from '../hooks/useCollections';
import { CollectionCard } from '../components/CollectionCard';
import { CreateCollectionModal } from '../components/CreateCollectionModal';
import { useNavigate } from 'react-router-dom';
import { SortSelector } from '../components/SortSelector';
import { usePermissions } from '../hooks/usePermissions';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import {
  sortCollections,
  loadSortPreference,
  saveSortPreference,
  SortOption,
  COLLECTION_SORT_OPTIONS,
  DEFAULT_COLLECTION_SORT,
} from '../utils/sortUtils';

export const Collections = () => {
  // The Albums page shows EVERY album the user can access (own + full
  // delegations), not just the active catalog's, so it reads the cross-catalog
  // union — and its OWN loading/error, not the active-catalog fetch's, so a
  // slow/failed union load shows a spinner/error rather than a false empty.
  const {
    allAccessibleCollections,
    allAccessibleLoading: isLoading,
    allAccessibleError: error,
    selectCollectionCrossCatalog,
  } = useCollections();
  const { permissions } = usePermissions();
  const { manageableCatalogs = [] } = useActiveCatalog();

  // Non-delegates (owners/admins) can always manage albums in their own
  // catalog, so their gate stays synchronous and never depends on the async
  // owned-catalogs fetch (avoiding a flash-hidden button, or a permanent lockout
  // if /api/subscription + /api/user/ali-id both fail). Delegates instead need
  // at least one manageable catalog — their own (if any) or an active full
  // delegation — which is exactly what `manageableCatalogs` enumerates (and what
  // the Create Album dialog's picker uses), so a delegate fully-delegating any
  // catalog can open the dialog even while active on a view-only one.
  const isViewer = permissions?.role === 'viewer';
  const isDelegate = permissions?.role === 'delegate';
  const canManageAlbums = !isViewer && (!isDelegate || manageableCatalogs.length > 0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const navigate = useNavigate();

  // Sort state with localStorage persistence
  // Load preference and filter to only include valid collection sort options
  const [sortOption, setSortOption] = useState<SortOption>(() => {
    const pref = loadSortPreference();
    // Check if the loaded preference is valid for collections (no artworkId sorting)
    const isValidForCollections = COLLECTION_SORT_OPTIONS.some(
      (opt) => opt.field === pref.field && opt.direction === pref.direction
    );
    return isValidForCollections ? pref : DEFAULT_COLLECTION_SORT;
  });

  const handleSortChange = useCallback((option: SortOption) => {
    setSortOption(option);
    saveSortPreference(option);
  }, []);

  // Apply sorting to collections
  const sortedCollections = useMemo(() => {
    return sortCollections(allAccessibleCollections ?? [], sortOption);
  }, [allAccessibleCollections, sortOption]);

  const handleSelectCollection = (collectionId: string) => {
    // Cross-catalog aware: switches the active catalog first when the album
    // lives in another catalog (GH#1233), so /my-objects renders the album in
    // ITS catalog rather than the one currently active.
    selectCollectionCrossCatalog(collectionId);
    navigate('/my-objects');
  };

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography color="error">Error loading albums: {error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          My Albums
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <SortSelector
            value={sortOption}
            onChange={handleSortChange}
            sortOptions={COLLECTION_SORT_OPTIONS}
          />
          {canManageAlbums && (
            <Button
              variant="contained"
              onClick={() => setCreateModalOpen(true)}
            >
              Create Album
            </Button>
          )}
        </Box>
      </Box>

      {isLoading ? (
        <Typography>Loading albums...</Typography>
      ) : sortedCollections.length === 0 ? (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" mb={2}>
            No albums yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={4}>
            Create your first album to organize your artwork
          </Typography>
          {canManageAlbums && (
            <Button
              variant="contained"
              onClick={() => setCreateModalOpen(true)}
            >
              Create Your First Album
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {sortedCollections.map(collection => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={collection.collectionId}>
              <CollectionCard
                collection={collection}
                onSelect={handleSelectCollection}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CreateCollectionModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
    </Container>
  );
};
